import PresentBar from "@/components/PresentBar";
import { presentingKey } from "@/lib/showcaseTour";
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE, apiHref } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, MapPin, Camera, PenLine, LogOut, RotateCcw, ExternalLink, Radio, Bell, BellOff, Droplets, Navigation, Phone, WifiOff, Wifi, Download, CheckCircle2, AlertTriangle, ClipboardCheck, Info } from "lucide-react";
import { toast } from "sonner";
import { DocketQR } from "@/components/DocketQR";
import { DRIVE_SAFE_OPTIONS, getDriveSafeId, setDriveSafeId, speakDriveSafe } from "@/lib/voice";
import { useOffline, drainQueue, getQueue, SYNCED_EVENT } from "@/lib/offline";

// Build a Google Maps directions URL from the truck to a site address. Works
// on Android/iOS/desktop — the OS opens the native maps app when possible.
function directionsUrl(address) {
  const dest = encodeURIComponent(address || "");
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
}

// ── Web Push helper (VAPID + service worker) ────────────────────────────────
function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function currentPushSubscription() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  const reg = await navigator.serviceWorker.ready;
  return await reg.pushManager.getSubscription();
}

async function subscribeToPush() {
  if (!("serviceWorker" in navigator)) throw new Error("Service workers not supported on this browser");
  if (!("PushManager" in window)) throw new Error("Push notifications not supported on this browser");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error(perm === "denied" ? "Notifications blocked — enable in browser settings" : "Permission not granted");
  const reg = await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/vapid-key");
  if (!data.key) throw new Error("Server missing VAPID key");
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.key),
  });
  const j = sub.toJSON();
  await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys });
  return sub;
}

async function unsubscribeFromPush() {
  const sub = await currentPushSubscription();
  if (!sub) return;
  await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
  await sub.unsubscribe();
}

export function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = getPos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.strokeStyle = "#FAFAFA";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    last.current = pos;
  };
  const end = () => {
    if (drawing.current) { drawing.current = false; onChange(canvasRef.current.toDataURL("image/png")); }
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    onChange("");
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={500}
        height={180}
        className="w-full bg-black border border-industrial touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <button onClick={clear} type="button" data-testid="clear-signature" className="text-xs mono uppercase text-muted-industrial hover:text-primary-orange flex items-center gap-1 mt-1"><RotateCcw className="w-3 h-3" /> Clear</button>
    </div>
  );
}

export default function DriverPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ trucks: [], dockets: [] });
  const [activeDocket, setActiveDocket] = useState(null);
  const [signature, setSignature] = useState("");
  const [signedBy, setSignedBy] = useState("");

  // Overlay actions still queued offline so a cached refresh never rewinds the driver's screen
  const applyPending = useCallback((d) => {
    const q = getQueue();
    if (!q.length || !d?.dockets) return d;
    let dockets = d.dockets;
    for (const it of q) {
      const m = (it.url || "").match(/^\/dockets\/([^/]+)\/(status|sign|water)$/);
      if (!m) continue;
      const [, id, kind] = m;
      let body = {}; try { body = JSON.parse(it.data || "{}") || {}; } catch { /* ignore */ }
      if (kind === "sign") dockets = dockets.filter((x) => x.id !== id);
      else dockets = dockets.map((x) => (x.id === id ? { ...x, ...(kind === "status" ? { status: body.status } : body) } : x));
    }
    return { ...d, dockets };
  }, []);
  const load = useCallback(() => api.get("/driver/my-dockets").then((r) => setData(applyPending(r.data))), [applyPending]);
  useEffect(() => {
    load();
    const i = setInterval(() => { if (navigator.onLine) load(); }, 10000);
    return () => clearInterval(i);
  }, [load]);

  // ─── v3.6v · Pre-Start Checklist (optional but strongly prompted) ───────
  // On mount we ask the backend if the driver has already submitted today.
  // If not, we pop the modal once. A dismissable banner stays in the header
  // until the driver either completes the check or opens/closes it manually.
  const [preStart, setPreStart] = useState({ truck: null, submitted: false, log: null });
  const [showPreStart, setShowPreStart] = useState(false);
  const [preStartDismissed, setPreStartDismissed] = useState(false);
  const loadPreStart = useCallback(async () => {
    try {
      const r = await api.get("/driver/pre-start/today");
      setPreStart(r.data);
      if (r.data.truck && !r.data.submitted && !preStartDismissed) setShowPreStart(true);
    } catch { /* driver may not have an assigned truck yet */ }
  }, [preStartDismissed]);
  useEffect(() => { loadPreStart(); }, [loadPreStart]);

  // ─── Live GPS ping while any docket is en_route or on_site ────────────────
  const [gpsStatus, setGpsStatus] = useState("idle"); // idle | requesting | tracking | denied | unsupported

  // ─── Push notifications ────────────────────────────────────────────────
  // pushStatus: unknown | unsupported | denied | off | on
  const [pushStatus, setPushStatus] = useState("unknown");
  useEffect(() => {
    (async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
        setPushStatus("unsupported");
        return;
      }
      if (Notification.permission === "denied") { setPushStatus("denied"); return; }
      const sub = await currentPushSubscription();
      setPushStatus(sub ? "on" : "off");
    })();
  }, []);

  const togglePush = async () => {
    try {
      if (pushStatus === "on") {
        await unsubscribeFromPush();
        setPushStatus("off");
        toast.success("Notifications off");
      } else {
        await subscribeToPush();
        setPushStatus("on");
        toast.success("Notifications on — you'll get a ping when jobs are dispatched");
      }
    } catch (e) {
      toast.error(e.message || "Could not update notifications");
      if (Notification.permission === "denied") setPushStatus("denied");
    }
  };

  const testPush = async () => {
    try {
      const res = await api.post("/push/test");
      if (res.data.sent > 0) toast.success(`Test sent to ${res.data.sent} device${res.data.sent > 1 ? "s" : ""}`);
      else toast.error("No active subscriptions — enable notifications first");
    } catch { toast.error("Failed to send test"); }
  };

  useEffect(() => {
    const activeDoc = data.dockets.find((d) => d.status === "en_route" || d.status === "on_site");
    const truckId = activeDoc?.truck_id || (data.trucks[0] && data.trucks[0].id);
    if (!activeDoc || !truckId) {
      setGpsStatus("idle");
      return;
    }
    if (!("geolocation" in navigator)) {
      setGpsStatus("unsupported");
      return;
    }
    setGpsStatus("requesting");
    let lastSent = 0;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setGpsStatus("tracking");
        const now = Date.now();
        if (now - lastSent < 8000) return; // throttle to every 8s
        lastSent = now;
        const { latitude, longitude, heading, speed } = pos.coords;
        api.post(`/trucks/${truckId}/location`, {
          lat: latitude,
          lng: longitude,
          heading: Number.isFinite(heading) ? heading : null,
          speed_kph: Number.isFinite(speed) ? speed * 3.6 : null,
          source: "driver_gps",
        }).catch(() => {});
      },
      () => setGpsStatus("denied"),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [data.dockets, data.trucks]);

  const doStatus = async (d, status) => {
    const res = await api.post(`/dockets/${d.id}/status`, { status }, { offline: `${d.docket_no} · ${status.replace("_", " ")}` });
    if (res.data?.queued) {
      setData((prev) => ({ ...prev, dockets: prev.dockets.map((x) => (x.id === d.id ? { ...x, status } : x)) }));
      return;
    }
    toast.success(`Marked ${status.replace("_", " ")}`);
    load();
  };

  const uploadPhoto = async (d, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/dockets/${d.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Photo uploaded");
    load();
  };

  const submitSignature = async () => {
    if (!activeDocket) return;
    if (!signedBy) return toast.error("Enter signer name");
    const res = await api.post(`/dockets/${activeDocket.id}/sign`, { signed_by: signedBy, signature_base64: signature }, { offline: `${activeDocket.docket_no} · signed by ${signedBy}` });
    if (res.data?.queued) {
      setData((prev) => ({ ...prev, dockets: prev.dockets.filter((x) => x.id !== activeDocket.id) }));
      setActiveDocket(null); setSignature(""); setSignedBy("");
      return;
    }
    toast.success("Delivery complete!");
    setActiveDocket(null); setSignature(""); setSignedBy(""); load();
  };

  const handleLogout = async () => { await logout(); nav("/login"); };

  // ─── Offline detection + install prompt · v3.5o ──────────────────────
  // Drivers regularly lose signal in tunnels / rural sites. When that
  // happens the service worker serves cached dockets and queues any
  // status pings. We also expose an "Install App" button so the driver
  // can add the portal to their home screen as a real Android/iOS app.
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const { pending, syncing, cached } = useOffline();
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(
    typeof window !== "undefined" && window.matchMedia("(display-mode: standalone)").matches
  );
  useEffect(() => {
    const goOn = () => {
      setOnline(true);
      // Replay any status stamps / signatures / notes recorded while offline
      drainQueue();
    };
    const goOff = () => setOnline(false);
    const handleInstallPrompt = (e) => { e.preventDefault(); setInstallEvent(e); };
    const handleInstalled = () => { setInstalled(true); setInstallEvent(null); toast.success("ArmorBatch Driver installed on your device"); };
    const handleSynced = () => { load(); loadPreStart(); };
    const handleSwMessage = (e) => { if (e.data?.type === "drain-now") drainQueue(); };
    window.addEventListener("online", goOn);
    window.addEventListener("offline", goOff);
    window.addEventListener("beforeinstallprompt", handleInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);
    window.addEventListener(SYNCED_EVENT, handleSynced);
    navigator.serviceWorker?.addEventListener("message", handleSwMessage);
    return () => {
      window.removeEventListener("online", goOn);
      window.removeEventListener("offline", goOff);
      window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
      window.removeEventListener(SYNCED_EVENT, handleSynced);
      navigator.serviceWorker?.removeEventListener("message", handleSwMessage);
    };
  }, [load, loadPreStart]);

  const installApp = async () => {
    if (!installEvent) {
      toast.info("Open the browser menu (⋮) and tap ‘Install app’ / ‘Add to Home Screen’");
      return;
    }
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === "accepted") setInstallEvent(null);
  };

  return (
    <div className="min-h-screen bg-[rgb(var(--bg))] pb-24" data-testid="driver-portal">
      {presentingKey() && <PresentBar />}
      {/* Header */}
      <div className="relative">
        <div className="h-40 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1644970213119-ee372456aaed?crop=entropy&cs=srgb&fm=jpg&q=85')" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/60 to-black" />
        <div className="absolute inset-0 flex flex-col justify-end p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-tag text-primary-orange">DRIVER PORTAL</div>
              <div className="heading text-2xl font-black uppercase">{user?.name}</div>
              <div className="mono text-xs text-muted-industrial">{data.trucks.map((t) => t.number).join(" · ") || "No truck assigned"}</div>
              <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                <span
                  data-testid="gps-status-pill"
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 border mono text-[9px] uppercase tracking-widest ${
                    gpsStatus === "tracking" ? "border-primary-orange text-primary-orange" :
                    gpsStatus === "requesting" ? "border-industrial text-muted-industrial" :
                    gpsStatus === "denied" || gpsStatus === "unsupported" ? "border-red-700 text-red-400" :
                    "border-industrial/60 text-muted-industrial"
                  }`}
                >
                  <Radio className={`w-2.5 h-2.5 ${gpsStatus === "tracking" ? "pulse-orange" : ""}`} />
                  GPS · {gpsStatus.toUpperCase()}
                </span>
                {/* Push notification toggle */}
                {pushStatus === "unsupported" ? (
                  <span data-testid="push-status-pill" className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-red-700 text-red-400 mono text-[9px] uppercase tracking-widest">
                    <BellOff className="w-2.5 h-2.5" /> PUSH · N/A
                  </span>
                ) : (
                  <button
                    type="button"
                    data-testid="push-toggle"
                    onClick={togglePush}
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 border mono text-[9px] uppercase tracking-widest ${
                      pushStatus === "on" ? "border-primary-orange text-primary-orange" :
                      pushStatus === "denied" ? "border-red-700 text-red-400" :
                      "border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange"
                    }`}
                  >
                    {pushStatus === "on" ? <Bell className="w-2.5 h-2.5" /> : <BellOff className="w-2.5 h-2.5" />}
                    PUSH · {pushStatus === "on" ? "ON" : pushStatus === "denied" ? "BLOCKED" : "TAP TO ENABLE"}
                  </button>
                )}
                {pushStatus === "on" && (
                  <button
                    type="button"
                    data-testid="push-test"
                    onClick={testPush}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-industrial hover:border-primary-orange text-muted-industrial hover:text-primary-orange mono text-[9px] uppercase tracking-widest"
                  >
                    Test
                  </button>
                )}
                {/* Online/offline pill · v3.5o */}
                <span
                  data-testid="online-status-pill"
                  title={online ? "Connected to dispatch" : "Offline — actions will sync when signal returns"}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 border mono text-[9px] uppercase tracking-widest ${
                    online
                      ? "border-emerald-500/60 text-emerald-400"
                      : "border-amber-500 text-amber-400 bg-amber-500/10 animate-pulse"
                  }`}
                >
                  {online ? <Wifi className="w-2.5 h-2.5" /> : <WifiOff className="w-2.5 h-2.5" />}
                  {online ? (cached ? "ONLINE · CACHED" : "ONLINE") : "OFFLINE · QUEUED"}
                </span>
                {/* Pending offline actions · v4.3 Failsafe */}
                {(pending.length > 0 || syncing) && (
                  <button
                    type="button"
                    data-testid="offline-pending-badge"
                    onClick={() => drainQueue()}
                    title={pending.map((p) => p.label).join("\n") || "Syncing…"}
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 border mono text-[9px] uppercase tracking-widest ${syncing ? "border-sky-400 text-sky-300" : "border-amber-500 text-amber-300 bg-amber-500/10"}`}
                  >
                    <RotateCcw className={`w-2.5 h-2.5 ${syncing ? "animate-spin" : ""}`} />
                    {syncing ? "SYNCING" : `${pending.length} PENDING SYNC`}
                  </button>
                )}
                {/* Install as app · v3.5o */}
                {!installed && (
                  <button
                    type="button"
                    data-testid="install-app-btn"
                    onClick={installApp}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-primary-orange text-primary-orange hover:bg-primary-orange hover:text-black mono text-[9px] uppercase tracking-widest"
                  >
                    <Download className="w-2.5 h-2.5" /> Install App
                  </button>
                )}
              </div>
            </div>
            <Button data-testid="driver-new-app" onClick={() => nav("/driver")} variant="outline" size="sm" className="rounded-none border-industrial bg-transparent text-ink text-[10px] mono uppercase">New app</Button>
            <Button data-testid="driver-logout" onClick={handleLogout} variant="outline" size="sm" className="rounded-none border-industrial bg-transparent text-ink"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Pre-start checklist banner — v3.6v */}
        {preStart.truck && !preStart.submitted && (
          <button
            data-testid="prestart-banner"
            onClick={() => { setPreStartDismissed(false); setShowPreStart(true); }}
            className="w-full text-left border-2 border-amber-500 bg-amber-500/10 p-3 hover:bg-amber-500/20 transition flex items-center gap-3"
          >
            <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0" />
            <div className="flex-1">
              <div className="mono text-[10px] uppercase tracking-widest text-amber-300 font-bold">Pre-Start Check Required</div>
              <div className="text-sm text-ink">Tap to run today's safety check on truck <span className="font-black text-amber-300">{preStart.truck.number}</span></div>
            </div>
            <ClipboardCheck className="w-5 h-5 text-amber-300" />
          </button>
        )}
        {preStart.truck && preStart.submitted && preStart.log?.overall === "pass" && (
          <div data-testid="prestart-done" className="border border-emerald-500/40 bg-emerald-500/5 p-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <div className="mono text-[10px] uppercase tracking-widest text-emerald-400">Pre-Start Passed · {preStart.truck.number}</div>
            <button
              type="button"
              className="ml-auto mono text-[10px] uppercase tracking-widest text-muted-industrial hover:text-primary-orange"
              data-testid="prestart-redo"
              onClick={() => { setPreStartDismissed(false); setShowPreStart(true); }}
            >
              Redo
            </button>
          </div>
        )}
        {preStart.truck && preStart.submitted && preStart.log?.overall === "fail" && (
          <div data-testid="prestart-fail" className="border-2 border-red-600 bg-red-950/30 p-3">
            <div className="mono text-[10px] uppercase tracking-widest text-red-400 font-bold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Pre-Start Fault Reported</div>
            <div className="text-sm text-ink mt-1">Dispatch has been notified. Truck flagged for maintenance.</div>
            {preStart.log?.fault_note && (
              <div className="mono text-[11px] text-red-300 mt-1">Note: {preStart.log.fault_note}</div>
            )}
          </div>
        )}

        {(data.assigned_jobs || []).length > 0 && (
          <>
            <div className="label-tag">Upcoming Assignments · {data.assigned_jobs.length}</div>
            {data.assigned_jobs.map((j) => (
              <div key={j.id} className="card-industrial p-4 border-l-2 border-primary-orange" data-testid={`driver-job-${j.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="mono text-xs text-primary-orange">{j.date} · {j.time_required || "any time"}</div>
                    <div className="heading font-black text-lg">{j.customer_name}</div>
                    {(j.driver_truck_numbers || []).length > 0 && (
                      <div className="mono text-[10px] uppercase text-muted-industrial mt-1">Your truck{(j.driver_truck_numbers || []).length > 1 ? "s" : ""}: <span className="text-primary-orange">{(j.driver_truck_numbers || []).join(" · ")}</span></div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="mono text-2xl font-bold">{j.quantity_m3}<span className="text-xs text-muted-industrial ml-1">m³</span></div>
                    <div className="mono text-[10px] uppercase text-muted-industrial" data-testid={`driver-job-mix-${j.id}`}>{j.mix_code}</div>
                    {j.slump_mm != null && (
                      <div className="mono text-[10px] uppercase text-primary-orange mt-0.5" data-testid={`driver-job-slump-${j.id}`}>Slump · {j.slump_mm} mm</div>
                    )}
                  </div>
                </div>
                {/* Big, tap-friendly delivery address + call/directions actions */}
                <div className="border border-industrial p-3 bg-surface-2/40 mt-2" data-testid={`driver-job-address-${j.id}`}>
                  <div className="label-tag text-[9px] mb-1">Deliver to</div>
                  <div className="text-base font-bold leading-snug flex items-start gap-1.5">
                    <MapPin className="w-4 h-4 mt-0.5 text-primary-orange shrink-0" />
                    <span>{j.site_address || j.site_name || "—"}</span>
                  </div>
                  {j.site_contact_phone && (
                    <div className="mono text-[11px] text-muted-industrial mt-1">
                      Site contact: {j.site_contact_name || "—"} · {j.site_contact_phone}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <a
                      href={directionsUrl(j.site_address || j.site_name)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`driver-job-directions-${j.id}`}
                      className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-wider text-xs font-bold text-black flex items-center justify-center gap-1.5 py-2.5"
                    >
                      <Navigation className="w-4 h-4" /> Directions
                    </a>
                    {j.site_contact_phone ? (
                      <a
                        href={`tel:${j.site_contact_phone}`}
                        data-testid={`driver-job-call-${j.id}`}
                        className="rounded-none border border-industrial bg-surface uppercase tracking-wider text-xs font-bold flex items-center justify-center gap-1.5 py-2.5 hover:border-primary-orange"
                      >
                        <Phone className="w-4 h-4" /> Call Site
                      </a>
                    ) : (
                      <div className="rounded-none border border-dashed border-industrial text-[10px] mono uppercase tracking-widest text-muted-industrial flex items-center justify-center py-2.5">
                        No contact
                      </div>
                    )}
                  </div>
                </div>
                {j.extras && (
                  <div className="border-l-2 border-[#FBBF24] pl-2 py-1 bg-[#FBBF24]/5 mt-2">
                    <div className="mono text-[9px] uppercase tracking-widest text-[#FBBF24]">Extras</div>
                    <div className="text-xs text-ink">{j.extras}</div>
                  </div>
                )}
                {j.driver_instructions && (
                  <div className="border-2 border-[#FBBF24] bg-[#FBBF24]/10 p-3 mt-2" data-testid={`driver-job-instructions-${j.id}`}>
                    <div className="mono text-[10px] uppercase tracking-widest text-[#FBBF24] font-black flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5" /> Site instructions
                    </div>
                    <div className="text-sm text-ink mt-1.5 leading-relaxed whitespace-pre-wrap">{j.driver_instructions}</div>
                  </div>
                )}
                <div className="text-[10px] mono uppercase text-muted-industrial mt-3">
                  Waiting for batching to load your truck. Docket will appear here once batched.
                </div>
              </div>
            ))}
          </>
        )}

        <div className="label-tag">Assigned Deliveries · {data.dockets.length}</div>

        {data.dockets.length === 0 && (data.assigned_jobs || []).length === 0 && <div className="border border-dashed border-industrial p-8 text-center text-muted-industrial">No active deliveries. Awaiting dispatch.</div>}
        {data.dockets.length === 0 && (data.assigned_jobs || []).length > 0 && <div className="text-xs text-muted-industrial">No loaded dockets yet — see upcoming assignments above.</div>}

        {data.dockets.map((d) => (
          <div key={d.id} className="card-industrial p-4" data-testid={`driver-docket-${d.docket_no}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="mono text-xs text-primary-orange">{d.docket_no}</div>
                <div className="heading font-black text-lg">{d.customer_name}</div>
              </div>
              <div className="text-right">
                <div className="mono text-2xl font-bold">{d.quantity_m3}<span className="text-xs text-muted-industrial ml-1">m³</span></div>
                <div className="mono text-[10px] uppercase text-muted-industrial" data-testid={`driver-docket-mix-${d.docket_no}`}>{d.mix_code}</div>
                {d.slump_mm != null && (
                  <div className="mono text-[10px] uppercase text-primary-orange mt-0.5" data-testid={`driver-docket-slump-${d.docket_no}`}>Slump · {d.slump_mm} mm</div>
                )}
              </div>
            </div>

            {/* Prominent site address + directions/call — always visible */}
            <div className="border border-industrial p-3 bg-surface-2/40 mb-3" data-testid={`driver-docket-address-${d.docket_no}`}>
              <div className="label-tag text-[9px] mb-1">Deliver to</div>
              <div className="text-base font-bold leading-snug flex items-start gap-1.5">
                <MapPin className="w-4 h-4 mt-0.5 text-primary-orange shrink-0" />
                <span>{d.site_address || "—"}</span>
              </div>
              {d.site_contact_phone && (
                <div className="mono text-[11px] text-muted-industrial mt-1">
                  Site contact: {d.site_contact_name || "—"} · {d.site_contact_phone}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 mt-3">
                <a
                  href={directionsUrl(d.site_address)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`driver-directions-${d.docket_no}`}
                  className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-wider text-xs font-bold text-black flex items-center justify-center gap-1.5 py-2.5"
                >
                  <Navigation className="w-4 h-4" /> Directions
                </a>
                {d.site_contact_phone ? (
                  <a
                    href={`tel:${d.site_contact_phone}`}
                    data-testid={`driver-call-${d.docket_no}`}
                    className="rounded-none border border-industrial bg-surface uppercase tracking-wider text-xs font-bold flex items-center justify-center gap-1.5 py-2.5 hover:border-primary-orange"
                  >
                    <Phone className="w-4 h-4" /> Call Site
                  </a>
                ) : (
                  <div className="rounded-none border border-dashed border-industrial text-[10px] mono uppercase tracking-widest text-muted-industrial flex items-center justify-center py-2.5">
                    No contact
                  </div>
                )}
              </div>
            </div>

            {/* Site instructions from dispatch — impossible to miss */}
            {d.driver_instructions && (
              <div className="border-2 border-[#FBBF24] bg-[#FBBF24]/10 p-3 mb-3" data-testid={`driver-instructions-${d.docket_no}`}>
                <div className="mono text-[10px] uppercase tracking-widest text-[#FBBF24] font-black flex items-center gap-1.5">
                  <Info className="w-3.5 h-3.5" /> Site instructions
                </div>
                <div className="text-sm text-ink mt-1.5 leading-relaxed whitespace-pre-wrap">{d.driver_instructions}</div>
              </div>
            )}

            <div className="grid grid-cols-4 gap-1 mb-4">
              {["loaded", "en_route", "on_site", "delivered"].map((s, i) => {
                const active = ["loaded", "en_route", "on_site", "delivered"].indexOf(d.status) >= i;
                return (
                  <div key={s} className={`h-1 ${active ? "bg-primary-orange" : "bg-surface-2"}`} />
                );
              })}
            </div>

            {/* Additional water & slump — appears once the driver has arrived on site.
                Rendered BEFORE the Sign & Complete action so the customer signs
                off on the water addition as part of the signature. */}
            {(d.status === "on_site" || d.status === "discharging") && (
              <WaterAddedPanel docket={d} onSaved={(local) => (local ? setData((prev) => ({ ...prev, dockets: prev.dockets.map((x) => (x.id === d.id ? { ...x, ...local } : x)) })) : load())} />
            )}

            <div className="grid grid-cols-2 gap-2 mt-4">
              {d.status === "loaded" && <Button data-testid={`depart-${d.docket_no}`} onClick={() => doStatus(d, "en_route")} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-wider text-xs col-span-2"><Truck className="w-4 h-4 mr-1" /> Depart Plant</Button>}
              {d.status === "en_route" && <Button data-testid={`arrive-${d.docket_no}`} onClick={() => doStatus(d, "on_site")} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-wider text-xs col-span-2"><MapPin className="w-4 h-4 mr-1" /> Arrived On Site</Button>}
              {d.status === "on_site" && (
                <>
                  <label data-testid={`photo-${d.docket_no}`} className="rounded-none border border-industrial bg-surface-2 uppercase tracking-wider text-xs text-ink flex items-center justify-center gap-2 py-2 cursor-pointer hover:border-primary-orange">
                    <Camera className="w-4 h-4" /> Photo
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadPhoto(d, e.target.files[0])} />
                  </label>
                  <Button data-testid={`sign-${d.docket_no}`} onClick={() => setActiveDocket(d)} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-wider text-xs"><PenLine className="w-4 h-4 mr-1" /> Sign & Complete</Button>
                </>
              )}
              <a href={apiHref(`/dockets/${d.id}/pdf`)} target="_blank" rel="noreferrer" className="text-xs mono uppercase text-primary-orange col-span-2 text-center mt-1 flex items-center justify-center gap-1"><ExternalLink className="w-3 h-3" /> View docket PDF</a>
            </div>
            <div className="mt-2 flex justify-center">
              <DocketQR docket={d} size="lg" label="Show QR to customer" />
            </div>
          </div>
        ))}
      </div>

      {/* Pre-Start Checklist modal — v3.6v */}
      {showPreStart && preStart.truck && (
        <PreStartModal
          truck={preStart.truck}
          onClose={() => { setShowPreStart(false); setPreStartDismissed(true); }}
          onSubmitted={(local) => {
            setShowPreStart(false);
            if (local?.queued) { setPreStart((p) => ({ ...p, submitted: true, log: { overall: local.overall, fault_note: local.fault_note } })); return; }
            loadPreStart(); load();
          }}
        />
      )}

      {/* Signature sheet */}
      {activeDocket && (
        <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center p-4 z-50" data-testid="signature-sheet">
          <div className="card-industrial p-5 w-full max-w-lg">
            <div className="label-tag mb-1">Sign for {activeDocket.customer_name}</div>
            <div className="heading text-lg font-black uppercase mb-3">Docket {activeDocket.docket_no}</div>
            {(() => {
              const slumpW = Number(activeDocket.slump_water_l || 0);
              const siteW = Number(activeDocket.site_water_adjust_l || 0);
              const totalAdd = slumpW + siteW;
              const qtyM3 = Math.max(1, Number(activeDocket.quantity_m3 || 0));
              const perM3 = totalAdd / qtyM3;
              const outsideAS = perM3 > 20 || totalAdd > 20;
              if (totalAdd <= 0 && !activeDocket.approx_slump_mm) return null;
              return (
                <div
                  data-testid="sign-water-summary"
                  className={`border p-3 mb-3 space-y-1 ${outsideAS ? "border-red-500 bg-red-950/40" : "border-primary-orange/40 bg-orange-950/10"}`}
                >
                  <div className={`mono text-[10px] uppercase tracking-widest font-bold ${outsideAS ? "text-red-400" : "text-primary-orange"}`}>
                    Customer acknowledges water added on site
                  </div>
                  <div className="mono text-xs">
                    Slump water: <span className="font-bold">{slumpW.toFixed(0)} L</span>
                    {" · "}Site adjust: <span className="font-bold">{siteW.toFixed(0)} L</span>
                    {" · "}Total: <span className="font-bold">{totalAdd.toFixed(0)} L ({perM3.toFixed(1)} L/m³)</span>
                  </div>
                  {activeDocket.approx_slump_mm ? (
                    <div className="mono text-[11px] text-muted-industrial">Measured slump after water: {activeDocket.approx_slump_mm} mm</div>
                  ) : null}
                  {outsideAS && (
                    <div className="text-[11px] text-red-300 font-bold leading-snug pt-1 border-t border-red-500/40 mt-1">
                      {"⚠ Water addition exceeds AS 1379 (>20 L/m³). By signing, the customer accepts responsibility for the water added on site and acknowledges the supplier takes NO responsibility for the strength or durability of concrete compromised by additional water beyond Australian Standards."}
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="space-y-3">
              <div>
                <Label className="label-tag">Signed by</Label>
                <Input data-testid="signed-by-input" value={signedBy} onChange={(e) => setSignedBy(e.target.value)} placeholder="Site foreman name" className="rounded-none bg-transparent border-industrial mono" />
              </div>
              <div>
                <Label className="label-tag">Signature</Label>
                <SignaturePad onChange={setSignature} />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setActiveDocket(null)} className="rounded-none flex-1 border-industrial bg-transparent uppercase tracking-wider text-xs">Cancel</Button>
                <Button data-testid="submit-signature" onClick={submitSignature} className="rounded-none flex-1 bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-wider text-xs">Complete Delivery</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Additional Water & Slump panel ──────────────────────────────────────────
// Driver-side record of any water added on the customer's site (slump chase
// or customer instruction) plus the measured slump after additions. Writes
// straight onto the docket via PATCH so the values appear on the docket PDF.
function WaterAddedPanel({ docket, onSaved }) {
  const [expanded, setExpanded] = useState(
    () => (docket.slump_water_l || 0) > 0 || (docket.site_water_adjust_l || 0) > 0 || !!docket.approx_slump_mm,
  );
  const [form, setForm] = useState({
    slump_water_l: docket.slump_water_l || 0,
    plant_water_l: docket.plant_water_l || 0,
    site_water_adjust_l: docket.site_water_adjust_l || 0,
    approx_slump_mm: docket.approx_slump_mm || "",
  });
  // Scientific Slump Log — live W/C from mix design (1 L water ≈ 1 kg)
  const wc = (() => {
    const cement = Number(docket.mix_cement_kg || docket.mix?.cement_kg || 0);
    const water = Number(docket.mix_water_kg || docket.mix?.water_kg || 0);
    const q = Math.max(0.1, Number(docket.quantity_m3 || 0));
    if (!cement) return null;
    const plant = Number(form.plant_water_l || 0) + Number(form.slump_water_l || 0);
    const site = Number(form.site_water_adjust_l || 0);
    return { design: water / cement, plant: (water * q + plant) / (cement * q), actual: (water * q + plant + site) / (cement * q) };
  })();
  const [saving, setSaving] = useState(false);

  // AS 1379 §7.2: post-batch water additions > 20 L/m³ can materially reduce
  // 28-day strength. Warn the driver in big red text before they save.
  const qty = Math.max(1, Number(docket.quantity_m3 || 0));
  const totalAdd = Number(form.slump_water_l || 0) + Number(form.plant_water_l || 0) + Number(form.site_water_adjust_l || 0);
  const perM3 = totalAdd / qty;
  const overStandard = perM3 > 20 || totalAdd > 20;

  const save = async () => {
    if (overStandard) {
      const ok = window.confirm(
        "WARNING · Water addition exceeds AS 1379 (>20 L/m³).\n\n" +
        "the supplier takes no responsibility for strength/durability of concrete " +
        "compromised by additional water beyond Australian Standards.\n\n" +
        "Continue saving?"
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      // Driver-safe endpoint — only water fields allowed
      const res = await api.patch(`/dockets/${docket.id}/water`, {
        slump_water_l: Number(form.slump_water_l || 0),
        plant_water_l: Number(form.plant_water_l || 0),
        site_water_adjust_l: Number(form.site_water_adjust_l || 0),
        approx_slump_mm: form.approx_slump_mm === "" ? null : Number(form.approx_slump_mm),
      }, { offline: `${docket.docket_no} · site water & notes` });
      if (!res.data?.queued) toast.success("Water added logged");
      onSaved && onSaved(res.data?.queued ? { ...docket, ...form } : null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 border border-industrial" data-testid={`water-panel-${docket.docket_no}`}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 bg-surface-2 hover:bg-surface uppercase mono text-[10px] tracking-widest"
      >
        <span className="flex items-center gap-2 text-primary-orange">
          <Droplets className="w-3.5 h-3.5" /> Additional Water & Slump
        </span>
        <span className="text-muted-industrial">{expanded ? "▲" : "▼"}</span>
      </button>
      {expanded && (
        <div className="p-3 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="label-tag">Plant Water (L)</Label>
              <Input
                type="number"
                step="1"
                min="0"
                inputMode="decimal"
                value={form.plant_water_l}
                onChange={(e) => setForm({ ...form, plant_water_l: e.target.value })}
                className="rounded-none bg-transparent border-industrial mono text-lg h-11"
                data-testid={`plant-water-${docket.docket_no}`}
              />
            </div>
            <div>
              <Label className="label-tag">Slump Water (L)</Label>
              <Input
                type="number"
                step="1"
                min="0"
                inputMode="decimal"
                value={form.slump_water_l}
                onChange={(e) => setForm({ ...form, slump_water_l: e.target.value })}
                className="rounded-none bg-transparent border-industrial mono text-lg h-11"
                data-testid={`slump-water-${docket.docket_no}`}
              />
            </div>
            <div>
              <Label className="label-tag">Site Adjust (L)</Label>
              <Input
                type="number"
                step="1"
                min="0"
                inputMode="decimal"
                value={form.site_water_adjust_l}
                onChange={(e) => setForm({ ...form, site_water_adjust_l: e.target.value })}
                className="rounded-none bg-transparent border-industrial mono text-lg h-11"
                data-testid={`site-water-${docket.docket_no}`}
              />
            </div>
          </div>
          <div>
            <Label className="label-tag">Approx Slump After Water (mm)</Label>
            <Input
              type="number"
              step="10"
              min="0"
              inputMode="decimal"
              value={form.approx_slump_mm}
              onChange={(e) => setForm({ ...form, approx_slump_mm: e.target.value })}
              placeholder="Measured on site"
              className="rounded-none bg-transparent border-industrial mono text-lg h-11"
              data-testid={`approx-slump-${docket.docket_no}`}
            />
          </div>
          {wc && (
            <div className="grid grid-cols-3 gap-1 border border-industrial p-2" data-testid={`wc-ratio-${docket.docket_no}`}>
              {[["Design W/C", wc.design], ["After plant", wc.plant], ["Actual on site", wc.actual]].map(([l, v]) => (
                <div key={l} className="text-center">
                  <div className="label-tag">{l}</div>
                  <div className={`mono text-base font-black ${v > wc.design + 0.05 ? "text-red-500" : v > wc.design + 0.001 ? "text-amber-400" : "text-emerald-500"}`}>{v.toFixed(3)}</div>
                </div>
              ))}
            </div>
          )}
          <div className="mono text-[10px] text-muted-industrial uppercase tracking-widest">
            Additional water reduces strength — record so the customer sees it on the docket.
          </div>
          {overStandard && (
            <div
              data-testid={`as1379-warning-${docket.docket_no}`}
              className="border-2 border-red-500 bg-red-950/60 p-3 space-y-1"
            >
              <div className="mono text-[10px] uppercase tracking-widest text-red-300 font-black">
                ⚠ Outside Australian Standards (AS 1379)
              </div>
              <div className="text-sm font-black text-red-400 leading-tight">
                Water addition exceeds 20 L/m³. the supplier takes NO responsibility for the strength or durability of concrete compromised by additional water beyond Australian Standards.
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-red-400/80">
                Total added: {totalAdd.toFixed(0)} L · {perM3.toFixed(1)} L/m³
              </div>
              {/* Why it failed — the numbers behind the warning */}
              <div className="grid grid-cols-2 gap-1 pt-1" data-testid={`as1379-breakdown-${docket.docket_no}`}>
                {(() => {
                  const cement = Number(docket.mix_cement_kg || docket.mix?.cement_kg || 0);
                  const water = Number(docket.mix_water_kg || docket.mix?.water_kg || 0);
                  const maxWc = Number(docket.mix_max_wc || docket.mix?.max_wc_ratio || 0) || (cement ? (water + 20) / cement : null);
                  const overL = Math.max(0, perM3 - 20) * qty;
                  return [
                    ["Current W/C", wc ? wc.actual.toFixed(2) : "—", "wc-current"],
                    ["Allowed Max W/C", maxWc ? maxWc.toFixed(2) : "—", "wc-max"],
                    ["Added water", `${perM3.toFixed(1)} L/m³`, "added"],
                    ["Allowed (AS 1379)", `20.0 L/m³ · over by ${overL.toFixed(0)} L`, "allowed"],
                  ].map(([l, v, id]) => (
                    <div key={id} className="bg-black/40 border border-red-900/60 px-2 py-1" data-testid={`as1379-${id}-${docket.docket_no}`}>
                      <div className="mono text-[8px] uppercase tracking-widest text-red-300/70">{l}</div>
                      <div className="mono text-sm font-black text-red-200 tabular-nums">{v}</div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
          <Button
            onClick={save}
            disabled={saving}
            data-testid={`save-water-${docket.docket_no}`}
            className={`w-full rounded-none uppercase tracking-widest text-xs font-bold h-11 ${overStandard ? "bg-red-600 hover:bg-red-700" : "bg-primary-orange hover:bg-[rgb(var(--primary-hover))]"}`}
          >
            {saving ? "Saving…" : overStandard ? "Log to Docket (Outside AS 1379)" : "Log to Docket"}
          </Button>
        </div>
      )}
    </div>
  );
}



// ── Pre-Start Checklist Modal · v3.6v ────────────────────────────────────────
// One-tap 8-point safety check driver runs before their shift. If anything is
// unticked (or fault notes are typed) the truck is auto-flagged for
// maintenance so dispatch sees it on the Fleet Health board.
export const CHECK_ITEMS = [
  { key: "oil",         label: "Engine Oil"    },
  { key: "coolant",     label: "Coolant Level" },
  { key: "tires",       label: "Tyres & Air"   },
  { key: "lights",      label: "Lights & Indicators" },
  { key: "brakes",      label: "Brakes / Airline" },
  { key: "mixer",       label: "Mixer / Drum" },
  { key: "hydraulics",  label: "Hydraulics"    },
  { key: "cleanliness", label: "Cleanliness"   },
];

export function PreStartModal({ truck, onClose, onSubmitted }) {
  const [checks, setChecks] = useState(() =>
    Object.fromEntries(CHECK_ITEMS.map((c) => [c.key, true]))
  );
  const [faultNote, setFaultNote] = useState("");
  const [hoursReading, setHoursReading] = useState("");
  const [kmReading, setKmReading] = useState("");
  const [saving, setSaving] = useState(false);
  const [driveSafe, setDriveSafe] = useState(getDriveSafeId());

  const failedItems = CHECK_ITEMS.filter((c) => !checks[c.key]).map((c) => c.label);
  const hasFault = failedItems.length > 0 || faultNote.trim().length > 0;

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        truck_id: truck.id,
        ...checks,
        fault_note: faultNote.trim(),
        hours_reading: hoursReading ? Number(hoursReading) : null,
        km_reading: kmReading ? Number(kmReading) : null,
      };
      const r = await api.post("/driver/pre-start", payload, { offline: `Pre-start · ${truck.number}` });
      if (r.data?.queued) {
        onSubmitted({ overall: hasFault ? "fail" : "pass", fault_note: faultNote.trim(), queued: true });
        return;
      }
      if (r.data.overall === "fail") {
        toast.error("Fault reported — dispatch has been notified", { duration: 6000 });
      } else {
        toast.success("Pre-start passed. Have a safe shift!");
      }
      onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit pre-start");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 flex items-end sm:items-center justify-center p-3 z-50" data-testid="prestart-modal">
      <div className="card-industrial p-4 w-full max-w-lg max-h-[95vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="label-tag text-primary-orange">Pre-Start Safety Check</div>
            <div className="heading text-xl font-black uppercase">Truck {truck.number}</div>
            <div className="mono text-[10px] uppercase text-muted-industrial mt-0.5">
              Tap each item to mark issues · uncheck = fault
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="rounded-none text-muted-industrial hover:text-ink"
            data-testid="prestart-close"
          >
            Skip
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-3">
          {CHECK_ITEMS.map((c) => {
            const ok = checks[c.key];
            return (
              <button
                key={c.key}
                type="button"
                data-testid={`prestart-${c.key}`}
                onClick={() => setChecks({ ...checks, [c.key]: !ok })}
                className={`p-3 border-2 text-left transition ${
                  ok
                    ? "border-emerald-500/70 bg-emerald-500/10 hover:bg-emerald-500/20"
                    : "border-red-600 bg-red-950/40 hover:bg-red-950/60"
                }`}
              >
                <div className="flex items-center gap-2">
                  {ok ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
                  )}
                  <div className="mono text-[11px] uppercase tracking-widest font-bold">
                    {c.label}
                  </div>
                </div>
                <div className={`mono text-[9px] uppercase tracking-widest mt-1 ${ok ? "text-emerald-400" : "text-red-400"}`}>
                  {ok ? "OK" : "Fault"}
                </div>
              </button>
            );
          })}
        </div>

        <div className="space-y-3 mb-3">
          <div>
            <Label className="label-tag">Fault / Notes (optional)</Label>
            <textarea
              data-testid="prestart-fault-note"
              value={faultNote}
              onChange={(e) => setFaultNote(e.target.value)}
              rows={2}
              placeholder="Describe any issue you noticed"
              className="w-full bg-transparent border border-industrial mono text-sm p-2 rounded-none focus:border-primary-orange focus:outline-none"
            />
          </div>
          <div className="mb-3" data-testid="prestart-drive-safe">
            <Label className="label-tag">Drive-safe call-out when you depart</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {DRIVE_SAFE_OPTIONS.map((o) => (
                <button key={o.id} type="button" data-testid={`prestart-drive-safe-${o.id}`}
                  onClick={() => { setDriveSafe(o.id); setDriveSafeId(o.id); speakDriveSafe(o.id); }}
                  className={`border px-3 py-3 text-left ${driveSafe === o.id ? "border-primary-orange bg-primary-orange/10" : "border-industrial"}`}>
                  <div className="heading text-sm font-black uppercase">{o.label}</div>
                  <div className="mono text-xs text-muted-industrial mt-0.5">{o.sample}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-tag">Engine Hours (optional)</Label>
              <Input
                type="number"
                inputMode="decimal"
                value={hoursReading}
                onChange={(e) => setHoursReading(e.target.value)}
                placeholder={String(truck.current_hours || 0)}
                className="rounded-none bg-transparent border-industrial mono h-11"
                data-testid="prestart-hours"
              />
            </div>
            <div>
              <Label className="label-tag">Odometer km (optional)</Label>
              <Input
                type="number"
                inputMode="decimal"
                value={kmReading}
                onChange={(e) => setKmReading(e.target.value)}
                placeholder={String(truck.current_km || 0)}
                className="rounded-none bg-transparent border-industrial mono h-11"
                data-testid="prestart-km"
              />
            </div>
          </div>
        </div>

        {hasFault && (
          <div className="border-2 border-red-600 bg-red-950/40 p-2 mb-3">
            <div className="mono text-[10px] uppercase tracking-widest text-red-400 font-bold flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> Fault will flag truck for maintenance
            </div>
            {failedItems.length > 0 && (
              <div className="mono text-[11px] text-red-300 mt-1">
                Issues: {failedItems.join(" · ")}
              </div>
            )}
          </div>
        )}

        <Button
          onClick={submit}
          disabled={saving}
          data-testid="prestart-submit"
          className={`w-full rounded-none uppercase tracking-widest text-sm font-black h-12 ${
            hasFault
              ? "bg-red-600 hover:bg-red-700 text-white"
              : "bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black"
          }`}
        >
          {saving ? "Submitting…" : hasFault ? "Report Fault & Flag Truck" : "Submit · All Good"}
        </Button>
      </div>
    </div>
  );
}
